import api from '@/services/api';
import { userService } from '@/services/user';
import { PaginatedResponse } from '@/types/common';
import {
  Report,
  ReportListItem,
  ReportForList,
  CreateReportData,
  UpdateReportData,
  AddEmailsData,
  ReportComponent,
  ReportWithContent,
  ReportMetrics,
  ReportMetricsParams,
  ReportViewItem,
  ReportStatus,
  ReportSendUser,
  SendReportPayload,
  ReportSaveTriggerSource,
} from '@/types/reports';
import { isAppError } from '@/lib/errors';
import { normalizeReportStatus } from '@/lib/reportStatus';
import { sortReportsByDateDesc } from '@/lib/sortReports';
import { normalizeReportLang, resolveReportLocale } from '@/pages/reports/i18n';

export interface CreateReportPayload {
  title: string;
  edition?: string;
  content: ReportComponent[];
  partner_id?: string;
  lang?: string;
}

export type { ReportSaveTriggerSource };

export interface UpdateReportPayload {
  title: string;
  edition?: string;
  content: ReportComponent[];
  partner_id?: string;
  status?: ReportStatus | 'AWAITING_SEND' | 'DRAFT' | 'FINISHED' | 'SENDING' | 'SENT' | 'SEND_FAILED';
  lang?: string;
  trigger_source?: ReportSaveTriggerSource;
}

const asOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const mapApiUserToReportSendUser = (item: Record<string, unknown>): ReportSendUser | null => {
  const nested =
    item.user && typeof item.user === 'object'
      ? (item.user as Record<string, unknown>)
      : {};
  const id =
    asOptionalString(item.user_id) ??
    asOptionalString(nested.id) ??
    asOptionalString(item.id);
  if (!id) return null;

  const whatsapp =
    asOptionalString(item.whatsapp) ?? asOptionalString(nested.whatsapp) ?? null;

  return {
    id,
    name: asOptionalString(item.name) ?? asOptionalString(nested.name) ?? '',
    email: asOptionalString(item.email) ?? asOptionalString(nested.email) ?? '',
    whatsapp,
    status: asOptionalString(item.status) ?? asOptionalString(nested.status) ?? '',
    already_sent: Boolean(item.already_sent ?? nested.already_sent),
  };
};

const normalizeEdition = (value: unknown): string | undefined => {
  if (value == null) return undefined;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const mapApiReportToReportWithContent = (apiReport: Report): ReportWithContent => {
  const metadata = apiReport.metadata || {};
  const partnerName = apiReport.partner_name ?? metadata.brand ?? null;
  const lang =
    apiReport.lang ??
    apiReport.language ??
    apiReport.language_code ??
    (typeof metadata.lang === 'string' ? metadata.lang : undefined) ??
    (typeof metadata.language === 'string' ? metadata.language : undefined);
  return {
    id: apiReport.id,
    title: metadata.title || apiReport.name,
    edition: normalizeEdition(metadata.edition),
    content: metadata.content || [],
    partner_id: apiReport.partner_id,
    partner_email_id: apiReport.partner_email_id,
    partner_name: partnerName,
    user_id: apiReport.user_id,
    createdAt: new Date(apiReport.created_at),
    updatedAt: apiReport.updated_at ? new Date(apiReport.updated_at) : new Date(apiReport.created_at),
    status: normalizeReportStatus(apiReport.status ?? metadata.status),
    lang,
    locale: resolveReportLocale({
      lang,
      language: apiReport.language,
      locale: apiReport.locale,
      language_code: apiReport.language_code,
      partner_name: partnerName,
      metadata,
    }),
  };
};

const mapReportListItemToReportForList = (item: ReportListItem): ReportForList => {
  const createdAt = new Date(item.created_at);
  const updatedAt = item.updated_at ? new Date(item.updated_at) : createdAt;

  return {
    id: item.id,
    title: item.name,
    edition: normalizeEdition(item.metadata?.edition),
    content: [],
    createdAt,
    updatedAt,
    partner_name: item.partner_name,
    visitor_count: item.visitor_count ?? 0,
    view_count: item.view_count ?? 0,
    status: normalizeReportStatus(item.status ?? item.metadata?.status),
  };
};

export const reportsService = {
  async create(data: CreateReportData): Promise<Report> {
    const response = await api.post<Report>('/reports', data);
    return response.data;
  },

  async createReport(payload: CreateReportPayload, email?: string): Promise<ReportWithContent> {
    const edition = normalizeEdition(payload.edition);
    const lang = normalizeReportLang(payload.lang);
    const data: CreateReportData = {
      name: payload.title,
      lang,
      metadata: {
        title: payload.title,
        ...(edition ? { edition } : {}),
        content: payload.content,
        lang,
      },
      ...(email ? { emails: [email] } : {}),
      ...(payload.partner_id ? { partner_id: payload.partner_id } : {}),
    };
    const response = await api.post<Report>('/reports', data);
    return mapApiReportToReportWithContent(response.data);
  },

  async getAllReports(params?: {
    limit?: number;
    offset?: number;
    q?: string;
    partner_id?: string;
    date_range?: string;
    start_date?: string;
    end_date?: string;
    sort_by?: string;
    order?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<ReportForList>> {
    const queryParams = new URLSearchParams();
    const offset = params?.offset ?? 0;
    const limit = params?.limit ?? 10;
    queryParams.append('offset', offset.toString());
    queryParams.append('limit', limit.toString());
    if (params?.q) queryParams.append('q', params.q);
    if (params?.partner_id) queryParams.append('partner_id', params.partner_id);
    if (params?.date_range) queryParams.append('date_range', params.date_range);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params?.order) queryParams.append('order', params.order);
    const url = `/reports?${queryParams.toString()}`;
    const response = await api.get<PaginatedResponse<ReportListItem>>(url);
    const mapped = response.data.data.map(mapReportListItemToReportForList);
    return {
      remaining: response.data.remaining,
      total: response.data.total,
      data: params?.sort_by ? mapped : sortReportsByDateDesc(mapped),
    };
  },

  async getReportById(id: string, email?: string): Promise<ReportWithContent | null> {
    try {
      let emailParam = email;
      if (!emailParam) {
        try {
          const user = await userService.getCurrentUser();
          emailParam = user?.email ?? undefined;
        } catch {
          emailParam = undefined;
        }
      }
      const queryParams = new URLSearchParams();
      if (emailParam) {
        queryParams.set('email', emailParam);
      }
      const queryString = queryParams.toString();
      const url = `/reports/${id}${queryString ? `?${queryString}` : ''}`;
      const response = await api.get<Report>(url);
      return mapApiReportToReportWithContent(response.data);
    } catch (error) {
      if (isAppError(error) && (error.status === 401 || error.status === 403)) {
        throw error;
      }
      console.error('Error getting report:', error);
      return null;
    }
  },

  async update(id: string, data: UpdateReportData): Promise<Report> {
    const response = await api.patch<Report>(`/reports/${id}`, data);
    return response.data;
  },

  async updateReport(id: string, payload: UpdateReportPayload): Promise<ReportWithContent> {
    const edition = normalizeEdition(payload.edition);
    const lang = normalizeReportLang(payload.lang);
    const data: UpdateReportData = {
      name: payload.title,
      lang,
      metadata: {
        title: payload.title,
        ...(edition ? { edition } : {}),
        content: payload.content,
        ...(payload.status ? { status: payload.status } : {}),
        lang,
      },
      ...(payload.partner_id ? { partner_id: payload.partner_id } : {}),
      ...(payload.status ? { status: payload.status } : {}),
      ...(payload.trigger_source ? { trigger_source: payload.trigger_source } : {}),
    };
    const response = await api.patch<Report>(`/reports/${id}`, data);
    return mapApiReportToReportWithContent(response.data);
  },

  async deleteReport(id: string): Promise<boolean> {
    try {
      await api.delete(`/reports/${id}`);
      return true;
    } catch (error) {
      console.error('Error deleting report:', error);
      return false;
    }
  },

  async duplicateReport(
    id: string,
    options?: { email?: string; title?: string }
  ): Promise<ReportWithContent | null> {
    try {
      const email = options?.email;
      const originalReport = await this.getReportById(id, email);
      if (!originalReport) {
        return null;
      }

      const duplicatedContent = originalReport.content.map(component => ({
        ...component,
        id: crypto.randomUUID(),
      }));

      const title = options?.title?.trim() || `${originalReport.title} (Cópia)`;

      return await this.createReport(
        {
          title,
          ...(originalReport.edition ? { edition: originalReport.edition } : {}),
          content: duplicatedContent,
          ...(originalReport.partner_id ? { partner_id: originalReport.partner_id } : {}),
          lang: normalizeReportLang(originalReport.lang),
        },
        email
      );
    } catch (error) {
      console.error('Error duplicating report:', error);
      return null;
    }
  },

  async addEmails(id: string, data: AddEmailsData): Promise<void> {
    await api.post(`/reports/${id}/emails`, data);
  },

  async getReportUsers(
    id: string,
    params?: { limit?: number; offset?: number; q?: string }
  ): Promise<PaginatedResponse<ReportSendUser>> {
    const queryParams = new URLSearchParams();
    queryParams.append('offset', String(params?.offset ?? 0));
    queryParams.append('limit', String(params?.limit ?? 10));
    if (params?.q) queryParams.append('q', params.q);
    const response = await api.get<PaginatedResponse<Record<string, unknown>> | Record<string, unknown>[]>(
      `/reports/${id}/users?${queryParams.toString()}`
    );
    const body = response.data;
    if (Array.isArray(body)) {
      return {
        data: body
          .map((item) => mapApiUserToReportSendUser(item))
          .filter((item): item is ReportSendUser => item !== null),
        total: body.length,
        remaining: 0,
      };
    }
    return {
      remaining: body.remaining,
      total: body.total,
      data: (body.data ?? [])
        .map((item) => mapApiUserToReportSendUser(item))
        .filter((item): item is ReportSendUser => item !== null),
    };
  },

  async sendReport(id: string, payload: SendReportPayload): Promise<void> {
    await api.post(`/reports/${id}/send`, payload);
  },

  async removeEmail(id: string, email: string): Promise<void> {
    await api.delete(`/reports/${id}/emails/${encodeURIComponent(email)}`);
  },

  async accessReport(id: string, data: { email: string; password: string }): Promise<ReportWithContent> {
    const response = await api.post<Report>(`/reports/${id}/access`, data);
    return mapApiReportToReportWithContent(response.data);
  },

  async addView(
    id: string,
    identifier: { user_id: string } | { partner_email_id: string }
  ): Promise<{ id?: string; view_id?: string } | void> {
    const response = await api.post<{ id?: string; view_id?: string }>(
      `/reports/${id}/views`,
      identifier
    );
    return response.data;
  },

  async updateViewDuration(
    reportId: string,
    viewId: string,
    durationMs: number
  ): Promise<void> {
    await api.patch(`/reports/${reportId}/views/${viewId}`, {
      duration: durationMs,
    });
  },

  async getMetrics(id: string, params?: ReportMetricsParams): Promise<ReportMetrics> {
    const queryParams = new URLSearchParams();
    if (params?.views_date_range) queryParams.append('views_date_range', params.views_date_range);
    if (params?.views_start_date) queryParams.append('views_start_date', params.views_start_date);
    if (params?.views_end_date) queryParams.append('views_end_date', params.views_end_date);
    if (params?.visitors_date_range) queryParams.append('visitors_date_range', params.visitors_date_range);
    if (params?.visitors_start_date) queryParams.append('visitors_start_date', params.visitors_start_date);
    if (params?.visitors_end_date) queryParams.append('visitors_end_date', params.visitors_end_date);
    const queryString = queryParams.toString();
    const url = `/reports/${id}/metrics${queryString ? `?${queryString}` : ''}`;
    const response = await api.get<ReportMetrics>(url);
    return response.data;
  },

  async getViews(id: string, params?: { limit?: number; offset?: number }): Promise<{ remaining: number; total: number; data: ReportViewItem[] }> {
    const queryParams = new URLSearchParams();
    queryParams.append('limit', String(params?.limit ?? 10));
    queryParams.append('offset', String(params?.offset ?? 0));
    const response = await api.get<{ remaining: number; total: number; data: ReportViewItem[] }>(`/reports/${id}/views?${queryParams.toString()}`);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/reports/${id}`);
  },
};
